import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Table,
  Loader,
  Center,
  Title,
  ScrollArea,
  Badge,
  Group,
  Text,
  Button,
  Modal,
  NumberInput,
  Textarea,
  Paper,
  Box,
  ThemeIcon,
  Tooltip,
  Grid,
  ActionIcon
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { 
  IconRefresh, 
  IconStethoscope, 
  IconBuilding,
  IconChefHat,
  IconPencil,
  IconPlus,
  IconCheck,
  IconX
} from "@tabler/icons-react";

export default function LaporanNutrisiPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // State Modal
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // State Form
  const [form, setForm] = useState({
    lemak: 0,
    protein: 0,
    karbohidrat: 0,
    catatan: ""
  });

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Mengambil data distribusi yang sudah include relasi 'laporanNutrisi'
      const response = await api.get("/supervisor/distribusi"); 
      
      // Handle format response Laravel
      const hasil = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      
      setData(hasil);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLER BUKA MODAL ---
  const handleOpenModal = (item) => {
    setSelectedItem(item);
    
    if (item.laporan_nutrisi) {
       // --- MODE EDIT ---
       setIsEditMode(true);
       setForm({
         lemak: Number(item.laporan_nutrisi.lemak),
         protein: Number(item.laporan_nutrisi.protein),
         karbohidrat: Number(item.laporan_nutrisi.karbohidrat),
         catatan: item.laporan_nutrisi.catatan || ""
       });
    } else {
       // --- MODE BARU ---
       setIsEditMode(false);
       setForm({ lemak: 0, protein: 0, karbohidrat: 0, catatan: "" });
    }
    
    open();
  };

  // --- 3. HANDLER SUBMIT ---
  const handleSubmit = async () => {
    if (!form.catatan) {
        alert("Mohon isi catatan hasil pemeriksaan.");
        return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        // Update Laporan Lama
        const laporanId = selectedItem.laporan_nutrisi.id;
        await api.put(`/supervisor/laporan-nutrisi/${laporanId}`, form);
      } else {
        // Buat Laporan Baru
        await api.post(`/supervisor/distribusi/${selectedItem.id}/nutrisi`, form);
      }

      alert("Laporan Nutrisi berhasil disimpan!");
      close();
      fetchData(); // Refresh data
    } catch (error) {
      console.error(error);
      alert("Gagal menyimpan laporan.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- 4. RENDER TABEL ---
  if (loading) return <Center h={300}><Loader size="lg" /></Center>;

  const rows = data.map((item) => {
    const hasReport = !!item.laporan_nutrisi; // Cek apakah laporan sudah ada

    return (
      <Table.Tr key={item.id}>
        {/* Kolom Tanggal */}
        <Table.Td>
          <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString('id-ID', {
                weekday: 'short', day: 'numeric', month: 'short'
            })}
          </Text>
          <Text fz="xs" c="dimmed">
            {new Date(item.tanggal_distribusi).getFullYear()}
          </Text>
        </Table.Td>

        {/* Kolom Info Distribusi (Sekolah & Menu) - STYLE BARU */}
        <Table.Td>
           <Group gap="sm">
             <ThemeIcon variant="light" color="blue" size="lg" radius="xl">
                <IconBuilding size={18}/>
             </ThemeIcon>
             <div>
                <Text fz="sm" fw={500}>{item.sekolah?.nama_sekolah || "Sekolah Dihapus"}</Text>
                <Group gap={5}>
                    <IconChefHat size={12} color="gray"/>
                    <Text fz="xs" c="dimmed">{item.menu?.nama_menu || "Menu Dihapus"}</Text>
                </Group>
             </div>
           </Group>
        </Table.Td>

        {/* Kolom Status Laporan */}
        <Table.Td>
           {hasReport ? (
             <Badge color="green" variant="light" leftSection={<IconCheck size={12}/>}>
                Sudah Diperiksa
             </Badge>
           ) : (
             <Badge color="gray" variant="outline" leftSection={<IconX size={12}/>}>
                Belum Input
             </Badge>
           )}
        </Table.Td>

        {/* Kolom Ringkasan Gizi */}
        <Table.Td>
           {hasReport ? (
             <Group gap={8}>
                <Tooltip label="Lemak"><Badge size="sm" color="yellow" variant="dot">{item.laporan_nutrisi.lemak}g</Badge></Tooltip>
                <Tooltip label="Protein"><Badge size="sm" color="red" variant="dot">{item.laporan_nutrisi.protein}g</Badge></Tooltip>
                <Tooltip label="Karbohidrat"><Badge size="sm" color="blue" variant="dot">{item.laporan_nutrisi.karbohidrat}g</Badge></Tooltip>
             </Group>
           ) : (
             <Text c="dimmed" fz="xs" fs="italic">- Data Kosong -</Text>
           )}
        </Table.Td>

        {/* Kolom Aksi - STYLE BARU */}
        <Table.Td>
           <Group gap="xs" justify="flex-end">
             <Tooltip label={hasReport ? "Edit Laporan" : "Input Data Baru"}>
                <ActionIcon 
                    variant={hasReport ? "subtle" : "filled"} 
                    color="blue"
                    onClick={() => handleOpenModal(item)}
                >
                    {hasReport ? <IconPencil size={16}/> : <IconPlus size={16}/>}
                </ActionIcon>
             </Tooltip>
           </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  // --- RENDER UTAMA ---
  return (
    <>
      <Group justify="space-between" mb="lg">
        <div>
           <Title order={2}>Laporan Nutrisi</Title>
           <Text c="dimmed" size="sm">Input dan pantau kualitas gizi makanan</Text>
        </div>
        <Button variant="light" onClick={fetchData} leftSection={<IconRefresh size={16}/>}>
            Refresh Data
        </Button>
      </Group>

      {/* Tabel dengan ScrollArea & Style Rapi */}
      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Tanggal</Table.Th>
                <Table.Th>Detail Distribusi</Table.Th>
                <Table.Th>Status Laporan</Table.Th>
                <Table.Th>Kandungan Gizi</Table.Th>
                <Table.Th ta="right">Aksi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
               {rows.length > 0 ? rows : (
                  <Table.Tr><Table.Td colSpan={5} align="center" c="dimmed" py="xl">Belum ada data distribusi.</Table.Td></Table.Tr>
               )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL FORM --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={
            <Group gap={10}>
                <ThemeIcon color="blue" variant="light"><IconStethoscope size={18}/></ThemeIcon>
                <Text fw={700}>{isEditMode ? "Edit Laporan Nutrisi" : "Input Laporan Baru"}</Text>
            </Group>
        }
        centered
        size="lg"
      >
        <Box>
           {/* Info Header di Modal */}
           {selectedItem && (
             <Paper withBorder p="sm" bg="gray.0" mb="md" radius="md">
                <Group justify="space-between">
                    <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Menu Makanan</Text>
                        <Text fw={500}>{selectedItem.menu?.nama_menu}</Text>
                    </div>
                    <div style={{textAlign:'right'}}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Sekolah Tujuan</Text>
                        <Text fw={500}>{selectedItem.sekolah?.nama_sekolah}</Text>
                    </div>
                </Group>
             </Paper>
           )}

           <Grid>
              <Grid.Col span={4}>
                  <NumberInput
                     label="Lemak (gram)"
                     placeholder="0"
                     min={0}
                     decimalScale={2}
                     value={form.lemak}
                     onChange={(val) => setForm({...form, lemak: val})}
                     withAsterisk
                  />
              </Grid.Col>
              <Grid.Col span={4}>
                  <NumberInput
                     label="Protein (gram)"
                     placeholder="0"
                     min={0}
                     decimalScale={2}
                     value={form.protein}
                     onChange={(val) => setForm({...form, protein: val})}
                     withAsterisk
                  />
              </Grid.Col>
              <Grid.Col span={4}>
                  <NumberInput
                     label="Karbohidrat (gram)"
                     placeholder="0"
                     min={0}
                     decimalScale={2}
                     value={form.karbohidrat}
                     onChange={(val) => setForm({...form, karbohidrat: val})}
                     withAsterisk
                  />
              </Grid.Col>
           </Grid>

           <Textarea
              label="Catatan / Kesimpulan"
              placeholder="Contoh: Kandungan gizi sudah sesuai standar..."
              minRows={3}
              mt="md"
              mb="lg"
              value={form.catatan}
              onChange={(e) => setForm({...form, catatan: e.target.value})}
              withAsterisk
           />

           <Group justify="flex-end">
              <Button variant="default" onClick={close}>Batal</Button>
              <Button onClick={handleSubmit} loading={submitting} leftSection={<IconCheck size={16}/>}>
                  Simpan Data
              </Button>
           </Group>
        </Box>
      </Modal>
    </>
  );
}